import { useCallback, useEffect, useState } from 'react';
import type { ColDef, GridApi } from 'ag-grid-community';
import {
  loadGridFilterOptions,
  type GridFilterOption,
  type GridFilterOptionRequest,
  type GridFilterOptionValue,
} from '../../utils/gridFilterOptions';

export interface ActiveColumnFilter {
  colId: string;
  label: string;
  summary: string;
}

const OPERATORS: Record<string, string> = {
  equals: '=',
  notEqual: '≠',
  contains: 'contains',
  notContains: 'does not contain',
  startsWith: 'starts with',
  endsWith: 'ends with',
  lessThan: '<',
  lessThanOrEqual: '≤',
  greaterThan: '>',
  greaterThanOrEqual: '≥',
  inRange: 'between',
  blank: 'is blank',
  notBlank: 'is not blank',
};

/** Display names for a column's filter values, keyed by `valueKey`. */
type ValueLabels = Map<string, string>;

function valueKey(value: unknown): string {
  return JSON.stringify(value ?? null);
}

function formatValue(value: unknown, labels?: ValueLabels): string {
  if (value === null || value === undefined || value === '') return '(Blanks)';
  return labels?.get(valueKey(value)) ?? String(value);
}

function summarizeCondition(model: Record<string, any>): string {
  const operator = OPERATORS[model.type] ?? model.type ?? '';
  if (model.type === 'blank' || model.type === 'notBlank') return operator;
  const from = model.filter ?? model.dateFrom;
  const to = model.filterTo ?? model.dateTo;
  if (model.type === 'inRange') return `${operator} ${formatValue(from)} and ${formatValue(to)}`;
  return `${operator} ${formatValue(from)}`.trim();
}

/**
 * Readable text for an AG Grid column filter model (built-in, multi and
 * checkbox set filters). Set values are shown by `labels` when given, so a
 * lookup column reads "Jordan Ross" rather than its rep id.
 */
export function summarizeFilterModel(model: Record<string, any> | null | undefined, labels?: ValueLabels): string {
  if (!model) return '';
  if (model.filterType === 'multi') {
    return (model.filterModels ?? [])
      .filter(Boolean)
      .map((child: Record<string, any>) => summarizeFilterModel(child, labels))
      .filter(Boolean)
      .join(' and ');
  }
  if (model.filterType === 'set') {
    const values: unknown[] = model.values ?? [];
    const format = (value: unknown) => formatValue(value, labels);
    const shown = values.length > 3
      ? `${values.slice(0, 2).map(format).join(', ')} +${values.length - 2} more`
      : values.map(format).join(', ');
    if (model.excluded) return `not ${shown}`;
    return values.length ? shown : 'none selected';
  }
  if (Array.isArray(model.conditions)) {
    const joiner = String(model.operator ?? 'AND').toLowerCase() === 'or' ? ' or ' : ' and ';
    return model.conditions.map(summarizeCondition).join(joiner);
  }
  if (model.type) return summarizeCondition(model);
  return 'filtered';
}

interface CheckboxOptionSource {
  staticOptions?: GridFilterOption[];
  optionRequest?: GridFilterOptionRequest;
}

/** The checkbox filter's option source, alone or inside a multi filter. */
function checkboxOptionSource(colDef: ColDef | undefined): CheckboxOptionSource | null {
  const params = colDef?.filterParams as Record<string, any> | undefined;
  if (!params) return null;
  const candidates: Record<string, any>[] = Array.isArray(params.filters)
    ? params.filters.map((child: Record<string, any>) => child?.filterParams ?? {})
    : [params];
  const source = candidates.find((candidate) => candidate.staticOptions || candidate.optionRequest);
  return source ? { staticOptions: source.staticOptions, optionRequest: source.optionRequest } : null;
}

function setFilterValues(model: Record<string, any>): GridFilterOptionValue[] {
  if (model.filterType === 'set') return model.values ?? [];
  if (model.filterType === 'multi') {
    return (model.filterModels ?? []).flatMap((child: Record<string, any> | null) => (child ? setFilterValues(child) : []));
  }
  return [];
}

const LABEL_PAGE_SIZE = 200;
const LABEL_MAX_PAGES = 5;

/**
 * Names for the given filter values. Only lookup columns (a label column, or
 * fixed choices) need this; their option lists are short, so this pages the
 * same cached option query the filter list uses until every value is found.
 */
async function resolveValueLabels(source: CheckboxOptionSource, values: GridFilterOptionValue[]): Promise<ValueLabels> {
  const wanted = new Set(values.filter((value) => value !== null && value !== '').map(valueKey));
  const labels: ValueLabels = new Map();
  const collect = (options: GridFilterOption[]) => {
    for (const option of options) {
      const key = valueKey(option.value);
      if (wanted.has(key)) labels.set(key, option.label);
    }
  };
  if (source.staticOptions) {
    collect(source.staticOptions);
    return labels;
  }
  if (!source.optionRequest?.labelColumnName) return labels;
  for (let page = 0; page < LABEL_MAX_PAGES && labels.size < wanted.size; page += 1) {
    const result = await loadGridFilterOptions({ ...source.optionRequest, limit: LABEL_PAGE_SIZE, offset: page * LABEL_PAGE_SIZE });
    collect(result.options);
    if (!result.hasMore) break;
  }
  return labels;
}

interface FilterEntry {
  colId: string;
  label: string;
  model: Record<string, any>;
  source: CheckboxOptionSource | null;
  localLabels?: ValueLabels;
}

/**
 * Client-side checkbox filters list the formatted cell text, so their chips use
 * the same text: the first loaded row holding each filtered value names it.
 */
function clientSideLabels(api: GridApi, colId: string, values: GridFilterOptionValue[]): ValueLabels | undefined {
  if ((api.getGridOption('rowModelType') ?? 'clientSide') !== 'clientSide') return undefined;
  const wanted = new Set(values.filter((value) => value !== null && value !== '').map(valueKey));
  if (wanted.size === 0) return undefined;
  const labels: ValueLabels = new Map();
  api.forEachLeafNode((rowNode) => {
    if (labels.size === wanted.size) return;
    const key = valueKey(api.getCellValue({ rowNode, colKey: colId }));
    if (!wanted.has(key) || labels.has(key)) return;
    const formatted = api.getCellValue({ rowNode, colKey: colId, useFormatter: true });
    if (formatted != null && formatted !== '') labels.set(key, String(formatted));
  });
  return labels;
}

function readFilterEntries(api: GridApi): FilterEntry[] {
  const model = api.getFilterModel() ?? {};
  return Object.entries(model)
    .filter(([, columnModel]) => columnModel)
    .map(([colId, columnModel]) => {
      const column = api.getColumn(colId);
      const label = (column && api.getDisplayNameForColumn(column, 'header')) || colId;
      const source = checkboxOptionSource(column?.getColDef());
      const modelValue = columnModel as Record<string, any>;
      const localLabels = column && !source ? clientSideLabels(api, colId, setFilterValues(modelValue)) : undefined;
      return { colId, label, model: modelValue, source, localLabels };
    });
}

/** Whether a lookup column's set values still need names before they are shown. */
function needsLabels(entry: FilterEntry): boolean {
  const source = entry.source;
  if (!source || !(source.staticOptions || source.optionRequest?.labelColumnName)) return false;
  return setFilterValues(entry.model).some((value) => value !== null && value !== '');
}

function toActiveFilter(entry: FilterEntry, labels?: ValueLabels): ActiveColumnFilter {
  if (!labels && needsLabels(entry)) {
    // Shown until the names load, instead of flashing raw ids.
    const count = setFilterValues(entry.model).length;
    return { colId: entry.colId, label: entry.label, summary: `${count} selected` };
  }
  return { colId: entry.colId, label: entry.label, summary: summarizeFilterModel(entry.model, labels ?? entry.localLabels) };
}

/**
 * The column filters a grid currently applies, kept in step with the grid.
 * Column filters persist (URL, saved layouts, dashboard state) and the column
 * carrying one can be scrolled out of view, so toolbars show them explicitly.
 */
const sameFilters = (a: ActiveColumnFilter[], b: ActiveColumnFilter[]): boolean =>
  a.length === b.length
  && a.every((filter, i) => filter.colId === b[i].colId && filter.label === b[i].label && filter.summary === b[i].summary);

export function useActiveColumnFilters() {
  // Set from the grid's onGridReady: a remounted grid hands over a new api.
  const [api, attach] = useState<GridApi | null>(null);
  const [filters, setFilterState] = useState<ActiveColumnFilter[]>([]);
  // REG-963: refresh runs on every displayedColumnsChanged / rowDataUpdated /
  // filterChanged event. A new array each time (even an empty one) re-rendered
  // the grid's page, the grid fired another of those events, and the page
  // re-rendered ~90 times a second for as long as it was open. That starved
  // React Router's navigation transition: sidebar clicks changed the URL but
  // never showed the new page until a hard refresh. Keep the previous array
  // when nothing changed, so the event round-trip ends.
  const setFilters = useCallback((next: ActiveColumnFilter[]) => {
    setFilterState((prev) => (sameFilters(prev, next) ? prev : next));
  }, []);

  useEffect(() => {
    if (!api || api.isDestroyed()) {
      setFilters([]);
      return;
    }
    // A destroyed grid drops its listeners; the next grid attaches itself.
    let generation = 0;
    // displayedColumnsChanged also fires when a re-render hands the grid new
    // column defs. Setting state on every event re-rendered the grid, which
    // fired the event again (about 26 times a second, forever), and that
    // stream of updates kept router navigations from ever rendering (REG-969).
    // Only act when the applied filters or their column names changed.
    let lastSignature: string | null = null;
    const refresh = () => {
      const entries = api.isDestroyed() ? [] : readFilterEntries(api);
      const signature = JSON.stringify(entries.map((entry) => [entry.colId, entry.label, entry.model,
        entry.localLabels ? [...entry.localLabels] : null]));
      if (signature === lastSignature) return;
      lastSignature = signature;
      const current = ++generation;
      setFilters(entries.map((entry) => toActiveFilter(entry)));
      const lookups = entries.filter(needsLabels);
      if (lookups.length === 0) return;
      void Promise.all(entries.map(async (entry) => {
        if (!needsLabels(entry)) return toActiveFilter(entry);
        try {
          return toActiveFilter(entry, await resolveValueLabels(entry.source!, setFilterValues(entry.model)));
        } catch {
          return toActiveFilter(entry, new Map());
        }
      })).then((resolved) => {
        if (current === generation && !api.isDestroyed()) setFilters(resolved);
      });
    };
    refresh();
    api.addEventListener('filterChanged', refresh);
    // Header names can arrive after the filter model (column defs load late).
    api.addEventListener('displayedColumnsChanged', refresh);
    // Client-side value names come from the rows, which can load after the filter.
    api.addEventListener('rowDataUpdated', refresh);
    return () => {
      generation += 1;
      if (api.isDestroyed()) return;
      api.removeEventListener('filterChanged', refresh);
      api.removeEventListener('displayedColumnsChanged', refresh);
      api.removeEventListener('rowDataUpdated', refresh);
    };
  }, [api, setFilters]);

  const removeFilter = useCallback((colId: string) => {
    if (!api || api.isDestroyed()) return;
    void api.setColumnFilterModel(colId, null).then(() => api.onFilterChanged());
  }, [api]);

  return { filters, removeFilter, attach };
}
